import Phaser from 'phaser';

/**
 * The Quest for the Evil Orb's send-off (spec
 * docs/superpowers/specs/2026-10-07-dark-shadows-three-levels-design.md §5):
 * "once you defeat him a cutscene plays where the evil orb floats from the
 * dead tree into the air and bursts into a white screen, bringing you to the
 * level complete screen".
 *
 * It plays ON the board rather than over a cover — the orb leaves the tree the
 * player has been defending round all level. On EVERY real win (the user's
 * call); the Evil Orb itself is a first-win prize claimed in GameScene, so
 * nothing in here touches progress.
 *
 * Heaven's Peak's Light Orb (spec 2026-10-13) plays the same send-off with
 * its own LOOK — the sakura ring's light-blue orb rising high over the
 * clouds, a pale blue-white halo — via `playOrbCutscene` and `LIGHT_ORB_LOOK`.
 *
 * An overlay over the host scene, in the style of `princeNachoCutscene.ts`:
 * the Level Complete screen lives inside GameScene, so `done` is called at the
 * white PEAK, the result screen builds underneath, and the white then fades
 * off it. A tap skips straight to the burst.
 */

const W = 1280;
const H = 720;
/** Above every GameScene layer AND its win overlay (50). */
const DEPTH = 500;

/** Beats, in ms. */
const T_RISE = 700;
const T_HOVER = 3300;
const T_BURST = 4600;
const WHITE_IN = 380;
const WHITE_OUT = 800;

/** How one orb's send-off looks; everything else is shared. */
export interface OrbLook {
  /** The orb's texture — the same sprite the board draws. */
  art: string;
  /** Where it comes to rest before it bursts, and how big it is there. */
  peak: { x: number; y: number };
  peakSize: number;
  /** The veil over the board. */
  veil: number;
  /** The halo's two alternating rings, and the rays before the burst. */
  halo: [number, number];
  ray: number;
  caption: string;
  captionColor: string;
  captionStroke: string;
  /** Prefix for every named object (`<name>`, `<name>-orb`, `<name>-white`). */
  name: string;
}

/** The Evil Orb: out of the dead tree to the middle of the screen, burning orange-red. */
export const EVIL_ORB_LOOK: OrbLook = {
  art: 'item-evilorb',
  peak: { x: W / 2, y: 320 },
  peakSize: 190,
  veil: 0x0a0004,
  halo: [0xff4a1a, 0xffa040],
  ray: 0xff7a3a,
  caption: 'The Evil Orb awakens...',
  captionColor: '#ffb48a',
  captionStroke: '#2a0606',
  name: 'evilorb-cutscene',
};

/**
 * The Light Orb (spec 2026-10-13): out of the sakura ring and UP into the
 * sunset sky — its spot on the board is already mid-screen, so it rests high —
 * glowing pale blue and white.
 */
export const LIGHT_ORB_LOOK: OrbLook = {
  art: 'decor-heavenorb',
  peak: { x: W / 2, y: 170 },
  peakSize: 190,
  veil: 0x06101c,
  halo: [0x9fd8ff, 0xffffff],
  ray: 0xcfeeff,
  caption: 'The Light Orb awakens...',
  captionColor: '#e6f6ff',
  captionStroke: '#0f2a44',
  name: 'lightorb-cutscene',
};

/**
 * Play the send-off. `from` is the orb in the tree (its position and size);
 * `hide` is called as the cutscene's own orb takes its place, so the board's
 * decor orb never shows twice. `done` is called exactly once, at the white
 * peak. The returned function cancels it all (GameScene's shutdown).
 */
export function playEvilOrbCutscene(
  scene: Phaser.Scene,
  from: { x: number; y: number; size: number },
  hide: () => void,
  done: () => void,
): () => void {
  return playOrbCutscene(scene, EVIL_ORB_LOOK, from, hide, done);
}

/** The shared send-off: rise, tremble, burst into white. See the file header. */
export function playOrbCutscene(
  scene: Phaser.Scene,
  look: OrbLook,
  from: { x: number; y: number; size: number },
  hide: () => void,
  done: () => void,
): () => void {
  const PEAK = look.peak;
  const PEAK_SIZE = look.peakSize;
  const objs: Phaser.GameObjects.GameObject[] = [];
  const timers: Phaser.Time.TimerEvent[] = [];
  let burst = false;
  let ended = false;

  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };
  const later = (ms: number, fn: () => void) => {
    timers.push(scene.time.delayedCall(ms, fn));
  };
  const teardown = () => {
    if (ended) return;
    ended = true;
    scene.input.off('pointerdown', skip);
    timers.forEach((t) => t.remove());
    objs.forEach((o) => {
      scene.tweens.killTweensOf(o);
      o.destroy();
    });
    objs.length = 0;
  };

  // The board dims; an interactive veil also stops a stray tap reaching a
  // tower button underneath.
  const veil = add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, look.veil, 0)
      .setDepth(DEPTH)
      .setInteractive()
      .setName(look.name),
  );
  scene.tweens.add({ targets: veil, fillAlpha: 0.55, duration: 600 });

  // The halo: a glow in the orb's colours drawn round it every frame.
  const halo = add(scene.add.graphics().setDepth(DEPTH + 1));
  const hasArt = scene.textures.exists(look.art);
  const orb = hasArt
    ? add(
        scene.add
          .image(from.x, from.y, look.art)
          .setDisplaySize(from.size, from.size)
          .setDepth(DEPTH + 2)
          .setName(`${look.name}-orb`),
      )
    : null;
  hide();

  const caption = add(
    scene.add
      .text(W / 2, 600, look.caption, {
        fontSize: '34px',
        fontStyle: 'bold',
        color: look.captionColor,
        stroke: look.captionStroke,
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 3)
      .setAlpha(0),
  );
  scene.tweens.add({ targets: caption, alpha: 1, duration: 600, delay: 400 });

  // The glow follows the orb and swells as the burst nears.
  let shake = 0;
  const glow = scene.time.addEvent({
    delay: 16,
    loop: true,
    callback: () => {
      if (!orb) return;
      const now = scene.time.now;
      const r = orb.displayWidth / 2;
      const pulse = 1 + Math.sin(now / (shake > 0 ? 60 : 220)) * 0.08;
      halo.clear();
      for (let i = 4; i >= 1; i--) {
        halo.fillStyle(i % 2 ? look.halo[0] : look.halo[1], 0.08 + shake * 0.04);
        halo.fillCircle(orb.x, orb.y, r * (1 + i * 0.28) * pulse);
      }
      if (shake > 0) {
        // Rays of light breaking out of it before the burst.
        const n = 10;
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2 + now / 600;
          const len = r * (1.6 + shake * 1.8);
          halo.lineStyle(6, look.ray, 0.35 * shake);
          halo.lineBetween(orb.x, orb.y, orb.x + Math.cos(a) * len, orb.y + Math.sin(a) * len);
        }
      }
    },
  });
  timers.push(glow);

  // ── Rise: out of the tree, up into the air, growing ────────────────────────
  if (orb) {
    later(T_RISE, () => {
      scene.tweens.add({
        targets: orb,
        x: PEAK.x,
        y: PEAK.y,
        displayWidth: PEAK_SIZE,
        displayHeight: PEAK_SIZE,
        duration: T_HOVER - T_RISE,
        ease: 'Sine.easeInOut',
      });
    });
    // ── Hover: it trembles, faster and faster ────────────────────────────────
    later(T_HOVER, () => {
      scene.tweens.add({
        targets: { v: 0 },
        v: 1,
        duration: T_BURST - T_HOVER,
        onUpdate: (tw) => {
          shake = tw.getValue() ?? 0;
          orb.setPosition(
            PEAK.x + Math.sin(scene.time.now / 23) * 7 * shake,
            PEAK.y + Math.cos(scene.time.now / 29) * 5 * shake,
          );
        },
      });
      scene.cameras.main.shake(T_BURST - T_HOVER, 0.004);
    });
  }

  // ── Burst: white floods the screen; the result screen builds at its peak ──
  const goBurst = () => {
    if (burst || ended) return;
    burst = true;
    timers.forEach((t) => t.remove());
    const white = add(
      scene.add
        .rectangle(W / 2, H / 2, W, H, 0xffffff, 0)
        .setDepth(DEPTH + 10)
        .setName(`${look.name}-white`),
    );
    if (orb) {
      scene.tweens.add({
        targets: orb,
        displayWidth: PEAK_SIZE * 3,
        displayHeight: PEAK_SIZE * 3,
        alpha: 0,
        duration: WHITE_IN,
        ease: 'Cubic.easeIn',
      });
    }
    scene.cameras.main.shake(300, 0.012);
    scene.tweens.add({
      targets: white,
      fillAlpha: 1,
      duration: WHITE_IN,
      onComplete: () => {
        // Everything but the white goes; the result screen builds under it.
        objs
          .filter((o) => o !== white)
          .forEach((o) => {
            scene.tweens.killTweensOf(o);
            o.destroy();
          });
        objs.length = 0;
        objs.push(white);
        scene.input.off('pointerdown', skip);
        done();
        scene.tweens.add({
          targets: white,
          fillAlpha: 0,
          duration: WHITE_OUT,
          onComplete: teardown,
        });
      },
    });
  };
  later(T_BURST, goBurst);

  const skip = () => goBurst();
  scene.input.on('pointerdown', skip);

  return teardown;
}
